import { useFormikContext } from "formik";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { FormikRadioYN, FormikTextarea } from "@/features/forms/components/formik-controls";
import type { Form8857Data } from "@/features/forms/types";

const NARRATIVES: { n: string; name: keyof Form8857Data; label: string }[] = [
  { n: "12", name: "inv_prep_ret_exp", label: "Describe your involvement in preparing the returns." },
  { n: "13", name: "person_income_know_exp", label: "Explain what you knew about the income of the person on line 6." },
  { n: "14", name: "ret_missed_info_exp", label: "Explain what you knew about any missing or incorrect information on the returns." },
  { n: "15", name: "show_bal_due_yes_exp", label: "If the returns showed a balance due, explain when and how you thought it would be paid." },
  { n: "16", name: "finance_problem_yes_exp", label: "Describe any financial problems you were having when the returns were filed." },
  { n: "17", name: "household_finance_exp", label: "Describe your involvement in the household finances and how money was spent." },
];

/**
 * Part III — Involvement with finances & preparing returns (legacy `sr_part3.php`).
 * Q11 intent-to-file-joint (radio + explain), Q12–17 narratives, Q18 large
 * expenses (radio + describe), Q19 transferred assets (radio + list).
 */
export function SrPart3({ onNext }: { onNext: () => void }) {
  const { values } = useFormikContext<Form8857Data>();

  return (
    <div className="flex flex-col gap-6">
      <Card className="flex flex-col gap-3 p-6">
        <span className="text-foreground text-sm">
          <span className="text-muted-foreground mr-2 font-mono">11</span>
          Did you intend to file a joint return for the tax year(s) on line 3?
        </span>
        <FormikRadioYN name="file_joint_return" aria-label="Intended to file a joint return" />
        <div className="grid gap-2 border-t pt-3">
          <Label htmlFor="file_return_explain">Explain why or why not</Label>
          <FormikTextarea name="file_return_explain" rows={3} />
        </div>
      </Card>

      {NARRATIVES.map((q) => (
        <Card key={q.name} className="grid gap-2 p-6">
          <Label htmlFor={q.name}>
            <span className="text-muted-foreground mr-2 font-mono">{q.n}</span>
            {q.label}
          </Label>
          <FormikTextarea name={q.name} rows={3} />
        </Card>
      ))}

      <Card className="flex flex-col gap-3 p-6">
        <span className="text-foreground text-sm">
          <span className="text-muted-foreground mr-2 font-mono">18</span>
          Did you or the person on line 6 incur any large purchases or expenses?
        </span>
        <FormikRadioYN name="incr_large_expense" aria-label="Incurred large expenses" />
        {values.incr_large_expense === "Y" && (
          <div className="grid gap-2 border-t pt-3">
            <Label htmlFor="incr_large_expense_exp">Describe the expenses/purchases</Label>
            <FormikTextarea name="incr_large_expense_exp" rows={3} />
          </div>
        )}
      </Card>

      <Card className="flex flex-col gap-3 p-6">
        <span className="text-foreground text-sm">
          <span className="text-muted-foreground mr-2 font-mono">19</span>
          Did the person on line 6 transfer any assets to you?
        </span>
        <FormikRadioYN name="transferred_assets" aria-label="Person transferred assets to you" />
        {values.transferred_assets === "Y" && (
          <div className="grid gap-2 border-t pt-3">
            <Label htmlFor="transferred_assets_exp">List the assets, dates, and values</Label>
            <FormikTextarea name="transferred_assets_exp" rows={3} />
          </div>
        )}
      </Card>

      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onNext}>
          Go to Part IV <ArrowRight />
        </Button>
      </div>
    </div>
  );
}
